import type { EntryKey } from "@pinia/colada";
import { useQuery, useQueryCache } from "@pinia/colada";
import rest from "@/api/rest";

// ============= Query Keys =============

export const PRICING_QUERY_KEYS = {
  root: ["pricing"] as const,
  plans: () => [...PRICING_QUERY_KEYS.root, "plans"] as const,
  organization: () => [...PRICING_QUERY_KEYS.root, "organization"] as const,
};

// ============= Queries =============

/**
 * Query for all pricing plans (public endpoint)
 * Returns pricing for all supported currencies
 *
 * @example
 * const plansQuery = usePricingPlansQuery();
 * const usdPlan = computed(() =>
 *   plansQuery.data.value?.find(p => p.currency === 'USD')
 * );
 */
export function usePricingPlansQuery() {
  return useQuery({
    key: PRICING_QUERY_KEYS.plans() as unknown as EntryKey,
    query: () => rest.pricing.getPlans(),
    staleTime: 1000 * 60 * 60, // 1 hour - pricing rarely changes
    gcTime: 1000 * 60 * 120, // 2 hours
  });
}

/**
 * Query for organization-specific pricing (authenticated endpoint)
 * Returns pricing for the user's organization currency
 *
 * @example
 * const orgPricingQuery = useOrganizationPricingQuery();
 * const models = computed(() => orgPricingQuery.data.value?.models ?? []);
 */
export function useOrganizationPricingQuery() {
  return useQuery({
    key: PRICING_QUERY_KEYS.organization() as unknown as EntryKey,
    query: () => rest.pricing.getOrganization(),
    staleTime: 1000 * 60 * 30, // 30 minutes - more dynamic than public pricing
    gcTime: 1000 * 60 * 60, // 1 hour
  });
}

// ============= Cache Invalidation =============

export function useInvalidatePricing() {
  const queryCache = useQueryCache();

  return {
    /**
     * Invalidate all pricing queries
     * Use when pricing data changes globally
     */
    all: () => queryCache.invalidateQueries({ key: PRICING_QUERY_KEYS.root as unknown as EntryKey }),

    /**
     * Invalidate only public pricing plans
     * Use when admin updates pricing configuration
     */
    plans: () =>
      queryCache.invalidateQueries({ key: PRICING_QUERY_KEYS.plans() as unknown as EntryKey }),

    /**
     * Invalidate only organization pricing
     * Use when user's organization plan changes (upgrade/downgrade)
     */
    organization: () =>
      queryCache.invalidateQueries({
        key: PRICING_QUERY_KEYS.organization() as unknown as EntryKey,
      }),
  };
}
